import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import analyticsErrorState from '../../../../../assets/images/analyticsErrorState.svg';
import {
  AnalyticsMetricError,
  AnalyticsMetricErrorTypesEnum,
} from '../../../../../models/api/analyticsMetricsModel';
import {EmptyState} from '../emptyState';

/*
 * Types.
 */

export type ErrorStateRenderer = RendererOf<{
  metricError: AnalyticsMetricError;
  width?: number;
  height?: number;
}>;

/*
 * Props.
 */

interface MetricErrorStateProps {
  error: AnalyticsMetricError;
  width?: number;
  height?: number;
}

/*
 * Constants.
 */

const genericErrorStateTitle = (
  <Trans id="analytics-metric-error-generic-title">Metric computation error</Trans>
);
const genericErrorStateSubtitle = (
  <Trans id="analytics-metric-error-generic-subtitle">
    Try reloading the report, and please contact support if errors continue
  </Trans>
);

const timeoutErrorStateTitle = <Trans id="analytics-metric-error-timeout-title">Metric timeout</Trans>;
const timeoutErrorStateSubtitle = (
  <Trans id="analytics-metric-error-timeout-subtitle">
    There are too many events to compute the metric, please try reducing the time period or use filters
  </Trans>
);

/*
 * Component.
 */

export const MetricErrorState: FC<MetricErrorStateProps> = ({error, width, height}) => {
  const {title, subtitle} = computeMetricErrorTitle(error);

  return (
    <EmptyState title={title} subtitle={subtitle} width={width} height={height} image={analyticsErrorState} />
  );
};

/*
 * Helpers.
 */

export const computeMetricErrorTitle = (
  error: AnalyticsMetricError,
): {title: ReactNode; subtitle: ReactNode} => {
  if (error.errorType === AnalyticsMetricErrorTypesEnum.TIMED_OUT) {
    return {title: timeoutErrorStateTitle, subtitle: timeoutErrorStateSubtitle};
  }

  return {title: genericErrorStateTitle, subtitle: genericErrorStateSubtitle};
};

export const defaultErrorStateRenderer: ErrorStateRenderer = ({metricError, height, width}) => (
  <MetricErrorState error={metricError} height={height} width={width} />
);
